import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, ChevronDown, Info, type LucideProps, OctagonAlert } from 'lucide-react';
import { Avatar as A, Separator as Sep } from 'radix-ui';
import type { ComponentType, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

/* --------------------------------------------------------------------- Card */

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-lg border bg-surface text-fg shadow-xs', className)} {...props} />;
}

export function CardHeader({
  title,
  description,
  actions,
  className,
  divider = true,
  as: Heading = 'h2',
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  divider?: boolean;
  as?: 'h2' | 'h3' | 'h4';
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-5 py-4', divider && 'border-b', className)}>
      <div className="min-w-0">
        <Heading className="text-md font-semibold leading-6 text-fg">{title}</Heading>
        {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-center justify-end gap-2 rounded-b-lg border-t bg-surface-2 px-5 py-3', className)} {...props} />;
}

export function Separator({ className, orientation = 'horizontal', ...props }: React.ComponentProps<typeof Sep.Root>) {
  return (
    <Sep.Root
      orientation={orientation}
      className={cn('shrink-0 bg-border', orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px', className)}
      {...props}
    />
  );
}

/* -------------------------------------------------------------------- Badge */

export const badgeStyles = cva(
  'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-medium leading-none [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-sunken text-muted',
        brand: 'border-brand-border bg-brand-subtle text-brand-text',
        success: 'border-success-border bg-success-bg text-success-fg',
        warning: 'border-warning-border bg-warning-bg text-warning-fg',
        danger: 'border-danger-border bg-danger-bg text-danger-fg',
        info: 'border-info-border bg-info-bg text-info-fg',
        rehab: 'border-rehab-border bg-rehab-bg text-rehab-fg',
      },
      size: {
        sm: 'h-5 px-2 text-xs [&_svg]:size-3',
        md: 'h-6 px-2.5 text-sm [&_svg]:size-3.5',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeStyles>['tone']>;

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeStyles> {
  icon?: ReactNode;
}

export function Badge({ className, tone, size, icon, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeStyles({ tone, size }), className)} {...props}>
      {icon}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------- Avatar */

const avatarSize = {
  xs: 'size-6 text-[0.625rem]',
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-lg',
  xl: 'size-20 text-2xl',
} as const;

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]![0]! : '';
  return (first + last).toUpperCase();
}

export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string;
  src?: string | null;
  size?: keyof typeof avatarSize;
  className?: string;
}) {
  return (
    <A.Root
      className={cn('inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-brand-subtle font-semibold text-brand-text', avatarSize[size], className)}
    >
      {src ? <A.Image src={src} alt="" className="size-full object-cover" /> : null}
      <A.Fallback delayMs={src ? 300 : 0} aria-hidden>
        {initialsOf(name)}
      </A.Fallback>
    </A.Root>
  );
}

/* ----------------------------------------------------------------- Skeleton */

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden
      className={cn(
        'animate-shimmer rounded-md bg-[linear-gradient(90deg,var(--surface-sunken)_25%,var(--border)_50%,var(--surface-sunken)_75%)] bg-[length:200%_100%]',
        className,
      )}
      {...props}
    />
  );
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} role="status" aria-label="Loading">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-3.5', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- EmptyState */

type IconComponent = ComponentType<LucideProps>;

export function EmptyState({
  icon: Icon,
  title,
  description,
  actions,
  size = 'md',
  className,
}: {
  icon?: IconComponent;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center text-center', size === 'md' ? 'px-6 py-14' : 'px-4 py-8', className)}>
      {Icon ? (
        <div className={cn('mb-4 grid place-items-center rounded-xl bg-brand-subtle text-brand-text', size === 'md' ? 'size-12' : 'size-10')}>
          <Icon className={size === 'md' ? 'size-6' : 'size-5'} aria-hidden />
        </div>
      ) : null}
      <h3 className="text-md font-semibold text-fg">{title}</h3>
      {description ? <p className="mt-1 max-w-sm text-base text-muted">{description}</p> : null}
      {actions ? <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{actions}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------------- ErrorState */

export function ErrorState({
  title = 'Something went wrong',
  reason,
  actions,
  details,
  requestId,
  className,
}: {
  title?: ReactNode;
  /** Plain-language explanation for a non-technical user. Never a stack trace. */
  reason?: ReactNode;
  actions?: ReactNode;
  /** Technical information, collapsed by default. */
  details?: string;
  requestId?: string;
  className?: string;
}) {
  return (
    <div role="alert" className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <div className="mb-4 grid size-12 place-items-center rounded-xl bg-danger-bg text-danger-fg">
        <OctagonAlert className="size-6" aria-hidden />
      </div>
      <h3 className="text-md font-semibold text-fg">{title}</h3>
      {reason ? (
        <p className="mt-1 max-w-md text-base text-muted">
          <span className="font-medium text-fg">Reason: </span>
          {reason}
        </p>
      ) : null}
      {actions ? <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{actions}</div> : null}
      {details || requestId ? (
        <details className="mt-6 max-w-lg text-left text-sm text-muted">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-subtle hover:text-fg">
            <ChevronDown className="size-3.5" aria-hidden />
            Technical details
          </summary>
          <div className="mt-2 rounded-md border bg-surface-2 p-3 font-mono text-xs">
            {requestId ? <div>Reference: {requestId}</div> : null}
            {details ? <div className="mt-1 break-words">{details}</div> : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------- Alert */

const alertTone = {
  info: { box: 'border-info-border bg-info-bg', icon: 'text-info-fg', Icon: Info },
  success: { box: 'border-success-border bg-success-bg', icon: 'text-success-fg', Icon: CheckCircle2 },
  warning: { box: 'border-warning-border bg-warning-bg', icon: 'text-warning-fg', Icon: AlertTriangle },
  danger: { box: 'border-danger-border bg-danger-bg', icon: 'text-danger-fg', Icon: OctagonAlert },
} as const;

export function Alert({
  tone = 'info',
  title,
  children,
  action,
  className,
  icon,
}: {
  tone?: keyof typeof alertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  icon?: ReactNode;
}) {
  const t = alertTone[tone];
  const Icon = t.Icon;
  return (
    <div role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'} className={cn('flex gap-3 rounded-lg border p-3.5', t.box, className)}>
      <span className={cn('mt-0.5 shrink-0 [&_svg]:size-4', t.icon)}>{icon ?? <Icon aria-hidden />}</span>
      <div className="min-w-0 flex-1 text-base text-fg">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className={cn('text-muted', title && 'mt-0.5')}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------- DescriptionList */

export function DescriptionList({
  items,
  columns = 2,
  className,
}: {
  items: { label: ReactNode; value: ReactNode }[];
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <dl className={cn('grid gap-x-8 gap-y-4', columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {items.map((item, i) => (
        <div key={i} className="min-w-0">
          <dt className="text-sm text-muted">{item.label}</dt>
          <dd className="mt-0.5 break-words text-base font-medium text-fg">{item.value || <span className="font-normal text-subtle">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}
